import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { CheckBoxRounded, InputSize } from '@progress/kendo-angular-inputs';
import { forkJoin, Observable } from 'rxjs';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';
import { EMAIL_VALIDATOR_REGEX } from 'src/app/extensions/constants/form-validator.constants';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { EmailAPIService } from 'src/app/extensions/services/api/email-api.service';
import { EmailsEvents } from 'src/app/extensions/services/api/events';
import { UserAPIService } from 'src/app/extensions/services/api/user-api.service';
import { isNotEmpty } from 'src/app/extensions/utilities';

@Component({
  selector: 'emails-add-edit',
  templateUrl: './emails-add-edit.component.html',
  styleUrls: ['./emails-add-edit.component.scss'],
  standalone: false,
})
export class EmailsAddEditComponent implements OnInit {
  @Input() emailId: string;

  public size: InputSize = 'large';
  public rounded: CheckBoxRounded = 'large';
  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';

  @ViewChild('addEmail') public addEmailform: NgForm;

  emailXRequestTypes: app.models.EmailXRequestTypes = {
    emailAddress: '',
    dictionaryAssetDocumentRequestTypeIds: [],
    dictionaryClientDataRequestTypeIds: [],
    dictionaryContractModificationRequestTypeIds: [],
  } as app.models.EmailXRequestTypes;

  allAssetDocumentRequestTypes: boolean = false;
  allContractModificationRequestTypes: boolean = false;
  allClientDataRequestTypes: boolean = false;

  dictionaryAssetDocumentRequestTypes: app.models.DictionaryRequestTypeModel[];
  dictionaryContractModificationRequestTypes: app.models.DictionaryRequestTypeModel[];
  dictionaryClientDataRequestTypes: app.models.DictionaryRequestTypeModel[];

  hasRequestTypeNotAssociated: boolean = false;

  // Track checkbox states (mapping requestType.id to boolean)
  assetDocumentRequestTypeCheckboxStates: { [id: string]: boolean } = {};
  contractModificationRequestTypeCheckboxStates: { [id: string]: boolean } = {};
  clientDataRequestTypeCheckboxStates: { [id: string]: boolean } = {};

  placeholder: string;

  emailValidator = EMAIL_VALIDATOR_REGEX;

  adminUsers: app.models.AdminUsersListModel[] = [];
  enableEmailConfigurationForAdminsOnly: boolean;

  public constructor(
    private _activeModal: NgbActiveModal,
    private _translateService: TranslateService,
    private _emailAPIService: EmailAPIService,
    private _dictionaryAPIService: DictionaryAPIService,
    private _notificationService: NotificationService,
    private _userAPIService: UserAPIService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {
    this.enableEmailConfigurationForAdminsOnly = JSON.parse(
      this._envConfig.ENABLE_EMAIL_CONFIGURATION_FOR_ADMINS_ONLY || 'false'
    );
  }

  public ngOnInit(): void {
    const source: Observable<any>[] = [
      this._dictionaryAPIService.getDictionaryAssetDocumentRequestTypes(),
      this._dictionaryAPIService.getDictionaryContractModificationRequestType(),
      this._dictionaryAPIService.getDictionaryClientDataRequestTypes(),
    ];

    if (isNotEmpty(this.emailId)) {
      source.push(this._emailAPIService.getEmailXRequestTypesByEmailId(this.emailId));
    }

    if (this.enableEmailConfigurationForAdminsOnly) {
      source.push(this._userAPIService.getAdminUsers({} as app.models.GetAdminUsersPageFilterModel));
    }

    forkJoin(source).subscribe(
      ([
        dictionaryAssetDocumentRequestTypes,
        dictionaryContractModificationRequestTypes,
        dictionaryClientDataRequestTypes,
        ...optionalResults
      ]) => {
        this.dictionaryAssetDocumentRequestTypes = dictionaryAssetDocumentRequestTypes.map(x => {
          x.id = x.id.toLowerCase();
          return x;
        });
        this.dictionaryContractModificationRequestTypes = dictionaryContractModificationRequestTypes.map(x => {
          x.id = x.id.toLowerCase();
          return x;
        });
        this.dictionaryClientDataRequestTypes = dictionaryClientDataRequestTypes.map(x => {
          x.id = x.id.toLowerCase();
          return x;
        });

        this.hasRequestTypeNotAssociated =
          dictionaryAssetDocumentRequestTypes.some(x => !x.isAssociatedToEmail) ||
          dictionaryContractModificationRequestTypes.some(x => !x.isAssociatedToEmail) ||
          dictionaryClientDataRequestTypes.some(x => !x.isAssociatedToEmail);

        let emailXRequestTypes = null;
        let adminUsers = null;

        if (isNotEmpty(this.emailId)) {
          emailXRequestTypes = optionalResults.shift();
        } else {
          this.emailXRequestTypes.dictionaryAssetDocumentRequestTypeIds = this.dictionaryAssetDocumentRequestTypes.map(
            x => x.id
          );
          this.emailXRequestTypes.dictionaryClientDataRequestTypeIds = this.dictionaryClientDataRequestTypes.map(
            x => x.id
          );
          this.emailXRequestTypes.dictionaryContractModificationRequestTypeIds =
            this.dictionaryContractModificationRequestTypes.map(x => x.id);
        }

        if (this.enableEmailConfigurationForAdminsOnly) {
          adminUsers = optionalResults.shift();
        }

        if (adminUsers && adminUsers.data) {
          this.adminUsers = adminUsers.data;
        }

        if (emailXRequestTypes && emailXRequestTypes.data) {
          this.emailXRequestTypes = emailXRequestTypes.data;
          if (
            this.enableEmailConfigurationForAdminsOnly &&
            !this.adminUsers.some(x => x.email == this.emailXRequestTypes.emailAddress)
          ) {
            this.adminUsers.push({ email: this.emailXRequestTypes.emailAddress } as app.models.AdminUsersListModel);
          }
        }

        this.emailXRequestTypes.dictionaryAssetDocumentRequestTypeIds.forEach(id => {
          this.assetDocumentRequestTypeCheckboxStates[id] = true;
        });
        this.emailXRequestTypes.dictionaryClientDataRequestTypeIds.forEach(id => {
          this.clientDataRequestTypeCheckboxStates[id] = true;
        });
        this.emailXRequestTypes.dictionaryContractModificationRequestTypeIds.forEach(id => {
          this.contractModificationRequestTypeCheckboxStates[id] = true;
        });

        this.allAssetDocumentRequestTypes = this.syncAllRequestTypes(
          dictionaryAssetDocumentRequestTypes,
          this.emailXRequestTypes.dictionaryAssetDocumentRequestTypeIds
        );
        this.allContractModificationRequestTypes = this.syncAllRequestTypes(
          dictionaryContractModificationRequestTypes,
          this.emailXRequestTypes.dictionaryContractModificationRequestTypeIds
        );
        this.allClientDataRequestTypes = this.syncAllRequestTypes(
          dictionaryClientDataRequestTypes,
          this.emailXRequestTypes.dictionaryClientDataRequestTypeIds
        );
      }
    );
  }

  public closeHandler() {
    this._activeModal.dismiss(null);
  }

  public saveHandler() {
    if (!this.addEmailform.valid) {
      return;
    }

    this._emailAPIService.saveEmailXRequestTypes(this.emailXRequestTypes).subscribe(statusSaveResponse => {
      if (statusSaveResponse.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('users.add.notifications.userStatusChanged'),
          this._translateService.instant('common.success')
        );
        const email = statusSaveResponse.response.events.find(
          x => x.eventType === EmailsEvents.EmailXRequestTypesSaved
        ).eventBody;
        email.emailAddress = this.emailXRequestTypes.emailAddress;
        this._activeModal.close(email);
      }
    });
  }

  onAllRequestTypeCheckboxChange(
    isChecked: boolean,
    sourceList: app.models.DictionaryDataModel[],
    checkboxStates: { [id: string]: boolean },
    ids: string[]
  ): void {
    sourceList.forEach(requestType => {
      checkboxStates[requestType.id] = isChecked;
      if (isChecked && !ids.includes(requestType.id)) {
        ids.push(requestType.id);
      } else if (!isChecked) {
        const index = ids.indexOf(requestType.id);
        if (index > -1) {
          ids.splice(index, 1);
        }
      }
    });
  }

  syncAllRequestTypes(sourceList: app.models.DictionaryDataModel[], ids: string[]): boolean {
    return sourceList.every(x => ids.some(id => x.id === id));
  }

  onRequestTypeCheckboxChange(
    id: string,
    sourceList: app.models.DictionaryDataModel[],
    checkboxStates: { [id: string]: boolean },
    ids: string[]
  ): boolean {
    if (checkboxStates[id] && !ids.includes(id)) {
      ids.push(id);
    } else if (!checkboxStates[id]) {
      const index = ids.indexOf(id);
      if (index > -1) {
        ids.splice(index, 1);
      }
    }

    return this.syncAllRequestTypes(sourceList, ids);
  }
}
